import { useCallback, useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { Trash2, X } from "lucide-react";
import { MAP_COLORS } from "@/lib/map-colors";
import { removeMapLayers } from "@/lib/map-cleanup";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useQueryClient } from "@tanstack/react-query";
import {
  useBulkDeleteStreets,
  useBulkDeleteMatchedStreets,
  getListStreetsQueryKey,
  getListMatchedStreetsQueryKey,
  getGetWorkspaceQueryKey,
} from "@/api/orval";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";

import type { FeatureCollection } from "@/types/geojson";

interface StreetDeleteToolProps {
  map: maplibregl.Map | null;
  streets: FeatureCollection | undefined;
  matchedStreets: FeatureCollection | undefined;
  active: boolean;
  onToggle: () => void;
  // When true the trigger button is rendered elsewhere (the bottom-left tools
  // control), so this component renders only its active-mode panel and logic.
  hideTrigger?: boolean;
}

export const StreetDeleteTool = ({
  map,
  streets,
  matchedStreets,
  active,
  onToggle,
  hideTrigger = false,
}: StreetDeleteToolProps) => {
  const [selectedStreetIds, setSelectedStreetIds] = useState<number[]>([]);
  // Orphaned matched coverage (a match whose street is no longer active) can't
  // be selected as a street, so it is tracked and deleted separately.
  const [selectedMatchedIds, setSelectedMatchedIds] = useState<number[]>([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const lassoPoints = useRef<[number, number][]>([]);
  const { workspaceId } = useCurrentWorkspace();
  const deleteStreetsMutation = useBulkDeleteStreets();
  const deleteMatchedMutation = useBulkDeleteMatchedStreets();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const selectedCount = selectedStreetIds.length + selectedMatchedIds.length;

  // Selection function
  const selectStreetsInLasso = useCallback(() => {
    if (lassoPoints.current.length === 0) return;

    const polygon = lassoPoints.current;

    const hasPointInLasso = (
      feature: FeatureCollection["features"][number],
    ) => {
      if (
        feature.geometry.type !== "LineString" &&
        feature.geometry.type !== "MultiLineString"
      ) {
        return false;
      }
      const coords: [number, number][] =
        feature.geometry.type === "LineString"
          ? (feature.geometry.coordinates as [number, number][])
          : (feature.geometry.coordinates as [number, number][][]).flat();
      return coords.some((coord) => pointInPolygon(coord, polygon));
    };

    const selectedStreets: number[] = [];
    streets?.features.forEach((feature) => {
      if (hasPointInLasso(feature)) selectedStreets.push(feature.id);
    });
    if (selectedStreets.length > 0) {
      setSelectedStreetIds((prev) => [
        ...new Set([...prev, ...selectedStreets]),
      ]);
    }

    // Also grab orphaned matched coverage in the lasso: matches whose street is
    // no longer active, so deleting the street can't reach them. Coverage still
    // sitting on an active street is left to the street's own deletion.
    const activeStreetIds = new Set(streets?.features.map((f) => f.id));
    const selectedMatched: number[] = [];
    matchedStreets?.features.forEach((feature) => {
      const streetId = feature.properties?.street_id;
      const isOrphan = streetId == null || !activeStreetIds.has(streetId);
      if (isOrphan && hasPointInLasso(feature))
        selectedMatched.push(feature.id);
    });
    if (selectedMatched.length > 0) {
      setSelectedMatchedIds((prev) => [
        ...new Set([...prev, ...selectedMatched]),
      ]);
    }
  }, [streets, matchedStreets]);

  const pointInPolygon = (
    point: [number, number],
    polygon: [number, number][],
  ): boolean => {
    const [x, y] = point;
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];

      const intersect =
        yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;

      if (intersect) inside = !inside;
    }

    return inside;
  };

  // Handle lasso selection
  useEffect(() => {
    if (!map || !active) return;

    // Change cursor
    map.getCanvas().style.cursor = active ? "crosshair" : "";

    const onMouseDown = (e: maplibregl.MapMouseEvent) => {
      if (!active) return;
      e.preventDefault();
      setIsDrawing(true);
      lassoPoints.current = [[e.lngLat.lng, e.lngLat.lat]];
    };

    const onMouseMove = (e: maplibregl.MapMouseEvent) => {
      if (!active || !isDrawing) return;
      lassoPoints.current.push([e.lngLat.lng, e.lngLat.lat]);

      // Draw the lasso path
      if (map.getSource("lasso")) {
        (map.getSource("lasso") as maplibregl.GeoJSONSource).setData({
          type: "Feature",
          geometry: {
            type: "LineString",
            coordinates: lassoPoints.current,
          },
          properties: {},
        });
      }
    };

    const onMouseUp = () => {
      if (!active || !isDrawing) return;
      setIsDrawing(false);

      // Close the lasso polygon
      if (lassoPoints.current.length > 2) {
        lassoPoints.current.push(lassoPoints.current[0]);
        selectStreetsInLasso();

        // Show the closed polygon with animated dashes
        if (map.getSource("lasso-polygon")) {
          (map.getSource("lasso-polygon") as maplibregl.GeoJSONSource).setData({
            type: "Feature",
            geometry: {
              type: "Polygon",
              coordinates: [lassoPoints.current],
            },
            properties: {},
          });
        }
      }

      // Clear the drawing line
      if (map.getSource("lasso")) {
        (map.getSource("lasso") as maplibregl.GeoJSONSource).setData({
          type: "FeatureCollection",
          features: [],
        });
      }

      lassoPoints.current = [];
    };

    map.on("mousedown", onMouseDown);
    map.on("mousemove", onMouseMove);
    map.on("mouseup", onMouseUp);

    return () => {
      map.off("mousedown", onMouseDown);
      map.off("mousemove", onMouseMove);
      map.off("mouseup", onMouseUp);
      map.getCanvas().style.cursor = "";
    };
  }, [map, active, isDrawing, selectStreetsInLasso]);

  // Add lasso layers
  useEffect(() => {
    if (!map || !active) return;

    // Drawing line (while dragging)
    if (!map.getSource("lasso")) {
      map.addSource("lasso", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        id: "lasso-line",
        type: "line",
        source: "lasso",
        paint: {
          "line-color": MAP_COLORS.delete,
          "line-width": 2,
          "line-dasharray": [4, 4],
        },
      });
    }

    // Closed polygon (after release) with marching ants
    if (!map.getSource("lasso-polygon")) {
      map.addSource("lasso-polygon", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Fill
      map.addLayer({
        id: "lasso-polygon-fill",
        type: "fill",
        source: "lasso-polygon",
        paint: {
          "fill-color": MAP_COLORS.delete,
          "fill-opacity": 0.1,
        },
      });

      // Outline with marching ants
      map.addLayer({
        id: "lasso-polygon-outline",
        type: "line",
        source: "lasso-polygon",
        paint: {
          "line-color": MAP_COLORS.delete,
          "line-width": 2,
          "line-dasharray": [4, 4],
        },
      });

      // Animate the marching ants with smooth movement
      let step = 0;

      const animate = () => {
        if (map.getLayer("lasso-polygon-outline")) {
          step = (step + 1) % 8;

          // Create smooth marching by shifting the pattern gradually
          const patterns = [
            [8, 4],
            [7, 4, 1, 0],
            [6, 4, 2, 0],
            [5, 4, 3, 0],
            [4, 4, 4, 0],
            [3, 4, 5, 0],
            [2, 4, 6, 0],
            [1, 4, 7, 0],
          ];

          map.setPaintProperty(
            "lasso-polygon-outline",
            "line-dasharray",
            patterns[step],
          );

          setTimeout(() => requestAnimationFrame(animate), 120);
        }
      };
      animate();
    }

    return () => {
      if (map.getLayer("lasso-line")) map.removeLayer("lasso-line");
      if (map.getSource("lasso")) map.removeSource("lasso");
      if (map.getLayer("lasso-polygon-fill"))
        map.removeLayer("lasso-polygon-fill");
      if (map.getLayer("lasso-polygon-outline"))
        map.removeLayer("lasso-polygon-outline");
      if (map.getSource("lasso-polygon")) map.removeSource("lasso-polygon");
    };
  }, [map, active]);

  // Update selected streets visualization with highlight
  useEffect(() => {
    if (!map || !streets) return;

    if (!map.getSource("selected-streets")) {
      map.addSource("selected-streets", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      // Outer glow for visibility - render on top
      map.addLayer({
        id: "selected-streets-glow",
        type: "line",
        source: "selected-streets",
        paint: {
          "line-color": MAP_COLORS.delete,
          "line-width": 8,
          "line-opacity": 0.3,
          "line-blur": 2,
        },
      });

      // Main selection line - render on top
      map.addLayer({
        id: "selected-streets-line",
        type: "line",
        source: "selected-streets",
        paint: {
          "line-color": MAP_COLORS.delete,
          "line-width": 5,
          "line-opacity": 1.0,
        },
        layout: {
          "line-cap": "round",
          "line-join": "round",
        },
      });
    }

    // Update the selected streets data, including any orphaned matched coverage
    // picked up by the lasso so the highlight reflects everything being deleted.
    const selectedFeatures = [
      ...streets.features.filter((f) => selectedStreetIds.includes(f.id)),
      ...(matchedStreets?.features.filter((f) =>
        selectedMatchedIds.includes(f.id),
      ) ?? []),
    ];

    (map.getSource("selected-streets") as maplibregl.GeoJSONSource).setData({
      type: "FeatureCollection",
      features: selectedFeatures,
    });

    return () =>
      removeMapLayers(
        map,
        ["selected-streets-glow", "selected-streets-line"],
        ["selected-streets"],
      );
  }, [map, streets, matchedStreets, selectedStreetIds, selectedMatchedIds]);

  const handleDelete = async () => {
    try {
      if (selectedStreetIds.length > 0) {
        await deleteStreetsMutation.mutateAsync({
          pathParams: { workspaceId: workspaceId ?? "" },
          data: { street_ids: selectedStreetIds },
        });
      }
      // Orphaned coverage has no street to delete, so remove it directly.
      if (selectedMatchedIds.length > 0) {
        await deleteMatchedMutation.mutateAsync({
          pathParams: { workspaceId: workspaceId ?? "" },
          data: { matched_ids: selectedMatchedIds },
        });
      }
      // Deleting hides the base streets and drops their matched coverage, so
      // refetch both layers plus the workspace progress the map reads from.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: getListStreetsQueryKey({ workspaceId: workspaceId ?? "" }),
        }),
        queryClient.invalidateQueries({
          queryKey: getListMatchedStreetsQueryKey({
            workspaceId: workspaceId ?? "",
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: getGetWorkspaceQueryKey({ workspaceId: workspaceId ?? "" }),
        }),
      ]);
      toast({
        title: "Success",
        description: `Deleted ${selectedCount} street(s)`,
      });
      setSelectedStreetIds([]);
      setSelectedMatchedIds([]);
      setShowConfirmDialog(false);
      onToggle(); // Exit delete mode
    } catch (_error) {
      toast({
        title: "Error",
        description: "Failed to delete streets",
        variant: "destructive",
      });
    }
  };

  const handleClearSelection = () => {
    setSelectedStreetIds([]);
    setSelectedMatchedIds([]);
    // Clear the lasso polygon visual
    if (map && map.getSource("lasso-polygon")) {
      (map.getSource("lasso-polygon") as maplibregl.GeoJSONSource).setData({
        type: "FeatureCollection",
        features: [],
      });
    }
  };

  return (
    <>
      {/* Delete button next to route button */}
      {!hideTrigger && (
        <div className="absolute right-4 top-4 z-10">
          <div className="rounded-md bg-white shadow">
            <Button
              variant={active ? "destructive" : "ghost"}
              className="w-[50px] border-l"
              size="icon"
              onClick={onToggle}
              title={active ? "Exit delete mode" : "Delete streets"}
            >
              {active ? (
                <X className="h-4 w-4" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      )}

      {/* Active delete mode controls */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="absolute bottom-4 left-14 z-10"
            initial={{
              opacity: 0,
              scale: 0.96,
              y: -8,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            exit={{
              opacity: 0,
              scale: 0.96,
              y: -8,
            }}
            transition={{
              duration: 0.15,
              ease: [0.16, 1, 0.3, 1], // Linear's signature easing
            }}
          >
            <div className="w-[300px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
              <div className="mb-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-gray-900">
                    Delete Mode
                  </h3>
                </div>
                <button
                  onClick={onToggle}
                  className="text-gray-400 transition-colors hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {selectedCount === 0 ? (
                <p className="text-sm text-gray-600">
                  Draw a lasso to select streets
                </p>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-md border border-red-200 bg-red-50 p-3">
                    <div className="text-sm font-medium text-red-900">
                      {selectedCount} street
                      {selectedCount !== 1 ? "s" : ""} selected
                    </div>
                    <div className="mt-0.5 text-xs text-red-700">
                      {selectedMatchedIds.length > 0
                        ? `Includes ${selectedMatchedIds.length} orphaned match${
                            selectedMatchedIds.length !== 1 ? "es" : ""
                          }`
                        : "Ready to delete"}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleClearSelection}
                      className="flex-1 text-sm"
                    >
                      Clear
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowConfirmDialog(true)}
                      className="flex-1 text-sm"
                      disabled={
                        deleteStreetsMutation.isPending ||
                        deleteMatchedMutation.isPending
                      }
                    >
                      <Trash2 className="mr-1 h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Streets</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedCount} street
              {selectedCount !== 1 ? "s" : ""}? Any matched coverage on them is
              removed too. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
